import React, { useState } from "react";
import { S, ACCENT } from "./theme";
import { dateInDays } from "./pipelineActions";
import DateBox from "./DateBox";

// Pop-up shown when a sales person taps Log Call / Log Demo / Won / Lost ...
// The person picks one fixed choice; extra boxes appear only when needed.
export default function ActionModal({ lead, action, onConfirm, onClose }) {
  const only = action.choices.length === 1 ? action.choices[0] : null;
  const [choiceId, setChoiceId] = useState(only ? only.id : "");
  const [note, setNote] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [subscription, setSubscription] = useState("");
  const [onboarding, setOnboarding] = useState("");
  const [revisitDate, setRevisitDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const choice = action.choices.find((c) => c.id === choiceId) || null;
  const goesToLost = !!choice && choice.nextStage === "Lost";

  const pick = (c) => {
    setChoiceId(c.id);
    setError("");
    setDueDate(c.askDate && c.task ? dateInDays(c.task.days) : "");
  };

  const submit = async () => {
    if (!choice) { setError("Please pick one option."); return; }
    if (choice.askDate && !dueDate) { setError("Please pick the follow-up date."); return; }
    if (choice.askValue && !(Number(subscription) > 0 || Number(onboarding) > 0)) {
      setError("Please enter the subscription or onboarding amount (in ₹).");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onConfirm({
        choice,
        note: note.trim(),
        dueDate,
        subscription: Number(subscription) || 0,
        onboarding: Number(onboarding) || 0,
        revisitDate,
      });
    } catch (e) {
      setError("Could not save: " + (e.message || e));
      setSaving(false);
    }
  };

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.modalBox} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#a78bfa", marginBottom: 4 }}>{action.title}</div>
        <div style={{ fontSize: 12, color: "#7b82a0", marginBottom: 14 }}>
          {lead.societyName}{lead.prospectName ? ` — ${lead.prospectName}` : ""}
        </div>

        {!only && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
            {action.choices.map((c) => {
              const selected = c.id === choiceId;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => pick(c)}
                  style={{
                    textAlign: "left", cursor: "pointer", fontSize: 13, padding: "9px 12px", borderRadius: 8,
                    background: selected ? "rgba(108,99,255,0.18)" : "#222638",
                    color: selected ? "#e8eaf0" : "#ccc",
                    border: `1px solid ${selected ? ACCENT : "#2e3250"}`,
                  }}
                >
                  {selected ? "● " : "○ "}{c.label}
                </button>
              );
            })}
          </div>
        )}

        {choice && choice.askDate && (
          <div style={{ marginBottom: 12 }}>
            <label style={S.label}>Follow-up date</label>
            <DateBox value={dueDate} onChange={setDueDate} />
          </div>
        )}

        {choice && choice.askValue && (
          <div style={S.formGrid}>
            <div>
              <label style={S.label}>Subscription (₹ / month)</label>
              <input style={S.input} type="number" value={subscription} onChange={(e) => setSubscription(e.target.value)} placeholder="0" />
            </div>
            <div>
              <label style={S.label}>Onboarding fee (₹, one-time)</label>
              <input style={S.input} type="number" value={onboarding} onChange={(e) => setOnboarding(e.target.value)} placeholder="0" />
            </div>
          </div>
        )}

        {goesToLost && (
          <div style={{ marginBottom: 12 }}>
            <label style={S.label}>Remind me to revisit on (optional)</label>
            <DateBox value={revisitDate} onChange={setRevisitDate} />
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <label style={S.label}>Short note (optional)</label>
          <input style={S.input} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything worth remembering" />
        </div>

        {error && <div style={{ color: "#f87171", fontSize: 12, marginBottom: 10 }}>{error}</div>}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button style={S.btnGray} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={S.btnBlue} onClick={submit} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}